import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Edit, Upload, Image as ImageIcon, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, API } from "@/lib/api";
import AdminLayout from "@/components/layout/AdminLayout";

function uploadFile(file, onProgress) {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (evt) => {
      if (onProgress && evt.total) onProgress(Math.round((evt.loaded / evt.total) * 100));
    },
    timeout: 0, // no timeout for large uploads
    maxContentLength: Infinity,
    maxBodyLength: Infinity,
  }).then((r) => r.data);
}

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function fullUrl(u) {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
}

export default function AdminCourseEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const [course, setCourse] = useState({ title: "", description: "", cover_url: "", status: "ativo", order: 0, is_public: false, external_url: "" });
  const [modules, setModules] = useState([]);
  const [moduleModal, setModuleModal] = useState(null); // { ...module }
  const [lessonModal, setLessonModal] = useState(null); // { module_id, ...lesson }
  const [coverUploading, setCoverUploading] = useState(false);
  const [videoUpload, setVideoUpload] = useState({ progress: 0, uploading: false, name: "" });
  const [thumbUploading, setThumbUploading] = useState(false);

  useEffect(() => {
    document.title = isNew ? "Novo curso — Admin" : "Editar curso — Admin";
    if (!isNew) {
      api.get(`/admin/courses/${id}`).then(({ data }) => {
        setCourse(data.course);
        setModules(data.modules);
      });
    }
  }, [id, isNew]);

  const saveCourse = async () => {
    try {
      if (isNew) {
        const { data } = await api.post("/admin/courses", course);
        toast.success("Curso criado");
        navigate(`/admin/cursos/editar/${data.id}`, { replace: true });
      } else {
        await api.put(`/admin/courses/${id}`, course);
        toast.success("Curso atualizado");
      }
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const reload = async () => {
    if (!id) return;
    const { data } = await api.get(`/admin/courses/${id}`);
    setCourse(data.course);
    setModules(data.modules);
  };

  const handleCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverUploading(true);
    try {
      const r = await uploadFile(file);
      setCourse({ ...course, cover_url: fullUrl(r.url) });
      toast.success("Capa carregada");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setCoverUploading(false); }
  };

  const saveModule = async () => {
    try {
      const payload = { course_id: id, title: moduleModal.title, description: moduleModal.description || "", order: moduleModal.order || modules.length + 1 };
      if (moduleModal.id) {
        await api.put(`/admin/modules/${moduleModal.id}`, payload);
      } else {
        await api.post(`/admin/modules`, payload);
      }
      toast.success("Módulo salvo");
      setModuleModal(null);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteModule = async (mid) => {
    if (!window.confirm("Excluir este módulo e todas as suas aulas?")) return;
    await api.delete(`/admin/modules/${mid}`);
    toast.success("Módulo excluído");
    reload();
  };

  const saveLesson = async () => {
    try {
      const payload = {
        module_id: lessonModal.module_id,
        title: lessonModal.title, description: lessonModal.description || "",
        video_url: lessonModal.video_url || "",
        thumbnail_url: lessonModal.thumbnail_url || "",
        allow_download: !!lessonModal.allow_download,
        materials: lessonModal.materials || [],
        duration: lessonModal.duration || "", order: lessonModal.order || 1, status: lessonModal.status || "ativo",
      };
      if (lessonModal.id) {
        await api.put(`/admin/lessons/${lessonModal.id}`, payload);
      } else {
        await api.post(`/admin/lessons`, payload);
      }
      toast.success("Aula salva");
      setLessonModal(null);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteLesson = async (lid) => {
    if (!window.confirm("Excluir esta aula?")) return;
    await api.delete(`/admin/lessons/${lid}`);
    toast.success("Aula excluída");
    reload();
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setVideoUpload({ progress: 0, uploading: true, name: `${file.name} (${formatBytes(file.size)})` });
    try {
      const r = await uploadFile(file, (p) => setVideoUpload((s) => ({ ...s, progress: p })));
      setLessonModal((prev) => ({ ...prev, video_url: fullUrl(r.url) }));
      toast.success("Vídeo carregado com sucesso");
    } catch (err) {
      toast.error(`Falha no upload: ${formatApiError(err)}`);
    } finally {
      setVideoUpload({ progress: 0, uploading: false, name: "" });
      e.target.value = ""; // allow re-uploading same file
    }
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setThumbUploading(true);
    try {
      const r = await uploadFile(file);
      setLessonModal((prev) => ({ ...prev, thumbnail_url: fullUrl(r.url) }));
      toast.success("Thumbnail carregada");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setThumbUploading(false);
      e.target.value = "";
    }
  };

  const addMaterial = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const r = await uploadFile(file);
      const materials = [...(lessonModal.materials || []), { name: file.name, url: fullUrl(r.url) }];
      setLessonModal({ ...lessonModal, materials });
      toast.success("Material adicionado");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <AdminLayout>
      <div className="p-8 lg:p-12 max-w-6xl mx-auto">
        <Link to="/admin/cursos" className="inline-flex items-center gap-2 text-sm mb-6" style={{ color: "var(--text-secondary)" }} data-testid="back-courses">
          <ArrowLeft size={14} /> Voltar
        </Link>

        <h1 className="font-serif text-4xl mb-8" style={{ color: "var(--text-primary)" }}>{isNew ? "Novo curso" : "Editar curso"}</h1>

        {/* Course form */}
        <div className="brand-card p-8 mb-8">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-1">
              <label className="text-xs uppercase tracking-widest font-semibold mb-2 block" style={{ color: "var(--copper)" }}>Capa</label>
              <div className="aspect-[4/3] rounded-xl overflow-hidden mb-3 border" style={{ borderColor: "var(--border)", background: "var(--bg)" }}>
                {course.cover_url ? (
                  <img src={course.cover_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><ImageIcon size={32} style={{ color: "var(--border)" }} /></div>
                )}
              </div>
              <label className="btn-secondary w-full cursor-pointer" data-testid="upload-cover-btn">
                <Upload size={14} /> {coverUploading ? "Enviando…" : "Enviar capa"}
                <input type="file" accept="image/*" hidden onChange={handleCover} />
              </label>
              <input className="brand-input mt-2 text-xs" placeholder="Ou cole URL da capa" value={course.cover_url || ""} onChange={(e) => setCourse({ ...course, cover_url: e.target.value })} />
            </div>
            <div className="md:col-span-2 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest font-semibold mb-2 block" style={{ color: "var(--copper)" }}>Título</label>
                <input className="brand-input" value={course.title} onChange={(e) => setCourse({ ...course, title: e.target.value })} data-testid="course-title-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-semibold mb-2 block" style={{ color: "var(--copper)" }}>Descrição</label>
                <textarea rows={4} className="brand-input" value={course.description} onChange={(e) => setCourse({ ...course, description: e.target.value })} data-testid="course-description-input" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest font-semibold mb-2 block" style={{ color: "var(--copper)" }}>Status</label>
                  <select className="brand-input" value={course.status} onChange={(e) => setCourse({ ...course, status: e.target.value })}>
                    <option value="ativo">Ativo</option>
                    <option value="inativo">Inativo</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest font-semibold mb-2 block" style={{ color: "var(--copper)" }}>Ordem</label>
                  <input type="number" className="brand-input" value={course.order || 0} onChange={(e) => setCourse({ ...course, order: parseInt(e.target.value) || 0 })} />
                </div>
                <div className="flex items-end gap-2">
                  <input type="checkbox" id="ispub" checked={!!course.is_public} onChange={(e) => setCourse({ ...course, is_public: e.target.checked })} className="w-5 h-5" />
                  <label htmlFor="ispub" className="text-sm pb-2.5" style={{ color: "var(--text-primary)" }}>Aberto a todas as alunas</label>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-semibold mb-2 block" style={{ color: "var(--copper)" }}>
                  Link externo (Kiwify, Hotmart, etc.)
                </label>
                <input
                  type="url"
                  className="brand-input"
                  placeholder="https://pay.kiwify.com.br/..."
                  value={course.external_url || ""}
                  onChange={(e) => setCourse({ ...course, external_url: e.target.value })}
                  data-testid="course-external-url-input"
                />
                <p className="text-xs mt-1.5" style={{ color: "var(--text-secondary)" }}>
                  Se preenchido, o botão público &quot;Acessar conteúdo&quot; redirecionará as visitantes para este link (ideal para vendas via Kiwify). Deixe vazio para usar o fluxo interno de login.
                </p>
              </div>
              <button onClick={saveCourse} className="btn-primary" data-testid="save-course-btn">
                {isNew ? "Criar curso" : "Salvar alterações"}
              </button>
            </div>
          </div>
        </div>

        {/* Modules */}
        {!isNew && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-serif text-2xl" style={{ color: "var(--text-primary)" }}>Módulos & Aulas</h2>
              <button onClick={() => setModuleModal({ title: "", description: "", order: modules.length + 1 })} className="btn-secondary" data-testid="new-module-btn">
                <Plus size={14} /> Novo módulo
              </button>
            </div>

            {modules.map((m, mi) => (
              <div key={m.id} className="brand-card p-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-serif italic text-sm" style={{ color: "var(--copper)" }}>Módulo {mi + 1}</div>
                    <h3 className="font-serif text-xl" style={{ color: "var(--text-primary)" }}>{m.title}</h3>
                    {m.description && <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>{m.description}</p>}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setModuleModal({ ...m })} className="p-2 rounded-lg hover:bg-[color:var(--bg)]" data-testid={`edit-module-${m.id}`}><Edit size={14} style={{ color: "var(--copper)" }} /></button>
                    <button onClick={() => deleteModule(m.id)} className="p-2 rounded-lg hover:bg-[color:var(--bg)]" data-testid={`delete-module-${m.id}`}><Trash2 size={14} style={{ color: "#dc2626" }} /></button>
                  </div>
                </div>

                <ul className="divide-y" style={{ borderColor: "var(--border)" }}>
                  {(m.lessons || []).map((l) => (
                    <li key={l.id} className="py-3 flex items-center gap-3">
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: l.status === "ativo" ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)", color: l.status === "ativo" ? "#16a34a" : "#dc2626" }}>{l.status}</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate" style={{ color: "var(--text-primary)" }}>{l.title}</div>
                        {l.duration && <div className="text-xs" style={{ color: "var(--text-secondary)" }}>{l.duration}</div>}
                      </div>
                      <button onClick={() => setLessonModal({ ...l })} className="p-2 rounded-lg hover:bg-[color:var(--bg)]" data-testid={`edit-lesson-${l.id}`}><Edit size={14} style={{ color: "var(--copper)" }} /></button>
                      <button onClick={() => deleteLesson(l.id)} className="p-2 rounded-lg hover:bg-[color:var(--bg)]" data-testid={`delete-lesson-${l.id}`}><Trash2 size={14} style={{ color: "#dc2626" }} /></button>
                    </li>
                  ))}
                </ul>
                <button onClick={() => setLessonModal({ module_id: m.id, title: "", description: "", video_url: "", materials: [], duration: "", order: (m.lessons?.length || 0) + 1, status: "ativo" })} className="mt-3 text-sm font-medium inline-flex items-center gap-1" style={{ color: "var(--copper)" }} data-testid={`add-lesson-${m.id}`}>
                  <Plus size={14} /> Adicionar aula
                </button>
              </div>
            ))}
          </div>
        )}

        {moduleModal && (
          <Modal onClose={() => setModuleModal(null)} title={moduleModal.id ? "Editar módulo" : "Novo módulo"}>
            <div className="space-y-3">
              <input className="brand-input" placeholder="Título" value={moduleModal.title} onChange={(e) => setModuleModal({ ...moduleModal, title: e.target.value })} data-testid="module-title" />
              <textarea rows={3} className="brand-input" placeholder="Descrição" value={moduleModal.description || ""} onChange={(e) => setModuleModal({ ...moduleModal, description: e.target.value })} />
              <input type="number" className="brand-input" placeholder="Ordem" value={moduleModal.order || 0} onChange={(e) => setModuleModal({ ...moduleModal, order: parseInt(e.target.value) || 0 })} />
              <button onClick={saveModule} className="btn-primary w-full" data-testid="save-module">Salvar</button>
            </div>
          </Modal>
        )}

        {lessonModal && (
          <Modal onClose={() => setLessonModal(null)} title={lessonModal.id ? "Editar aula" : "Nova aula"}>
            <div className="space-y-3">
              <input className="brand-input" placeholder="Título" value={lessonModal.title} onChange={(e) => setLessonModal({ ...lessonModal, title: e.target.value })} data-testid="lesson-title" />
              <textarea rows={3} className="brand-input" placeholder="Descrição" value={lessonModal.description || ""} onChange={(e) => setLessonModal({ ...lessonModal, description: e.target.value })} />
              <div className="space-y-2">
                <label className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--copper)" }}>Vídeo da aula</label>
                <input className="brand-input" placeholder="URL do YouTube/Vimeo ou caminho do arquivo" value={lessonModal.video_url || ""} onChange={(e) => setLessonModal({ ...lessonModal, video_url: e.target.value })} data-testid="lesson-video-url" />
                <label className={`btn-secondary cursor-pointer w-full ${videoUpload.uploading ? "opacity-70 pointer-events-none" : ""}`} data-testid="upload-video-btn">
                  <Upload size={14} /> {videoUpload.uploading ? `Enviando… ${videoUpload.progress}%` : "Ou enviar arquivo de vídeo (MP4, WebM)"}
                  <input type="file" accept="video/*" hidden onChange={handleVideoUpload} disabled={videoUpload.uploading} />
                </label>
                {videoUpload.uploading && (
                  <div className="mt-2" data-testid="video-upload-progress">
                    <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--bg)" }}>
                      <div className="h-full transition-all duration-200" style={{ width: `${videoUpload.progress}%`, background: "var(--copper)" }} />
                    </div>
                    <div className="text-[11px] mt-1 flex justify-between" style={{ color: "var(--text-secondary)" }}>
                      <span className="truncate">{videoUpload.name}</span>
                      <span>{videoUpload.progress}%</span>
                    </div>
                  </div>
                )}
                {lessonModal.video_url && !videoUpload.uploading && (
                  <div className="text-xs truncate" style={{ color: "var(--text-secondary)" }}>✓ {lessonModal.video_url}</div>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--copper)" }}>Thumbnail (imagem de capa do vídeo)</label>
                <div className="flex items-center gap-3">
                  {lessonModal.thumbnail_url && (
                    <img src={lessonModal.thumbnail_url} alt="" className="w-24 h-14 object-cover rounded-lg border" style={{ borderColor: "var(--border-light)" }} />
                  )}
                  <label className="btn-secondary cursor-pointer flex-1" style={{ fontSize: "0.85rem", padding: "0.5rem 1rem" }} data-testid="upload-thumb-btn">
                    <Upload size={12} /> {thumbUploading ? "Enviando…" : (lessonModal.thumbnail_url ? "Trocar thumbnail" : "Enviar thumbnail")}
                    <input type="file" accept="image/*" hidden onChange={handleThumbnailUpload} disabled={thumbUploading} />
                  </label>
                </div>
                <p className="text-[11px]" style={{ color: "var(--text-secondary)" }}>Aparece antes do vídeo dar play. Recomendado 1280×720.</p>
              </div>

              <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="allow-download-toggle">
                <input type="checkbox" className="w-4 h-4" checked={!!lessonModal.allow_download} onChange={(e) => setLessonModal({ ...lessonModal, allow_download: e.target.checked })} />
                <span style={{ color: "var(--text-primary)" }}>Permitir que a aluna baixe este vídeo</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                <input className="brand-input" placeholder="Duração (ex: 12 min)" value={lessonModal.duration || ""} onChange={(e) => setLessonModal({ ...lessonModal, duration: e.target.value })} />
                <input type="number" className="brand-input" placeholder="Ordem" value={lessonModal.order || 1} onChange={(e) => setLessonModal({ ...lessonModal, order: parseInt(e.target.value) || 1 })} />
              </div>
              <select className="brand-input" value={lessonModal.status} onChange={(e) => setLessonModal({ ...lessonModal, status: e.target.value })}>
                <option value="ativo">Ativa</option>
                <option value="inativo">Inativa</option>
              </select>
              <div>
                <label className="text-xs uppercase tracking-widest font-semibold mb-2 block" style={{ color: "var(--copper)" }}>Materiais de apoio</label>
                <div className="space-y-2">
                  {(lessonModal.materials || []).map((m, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 rounded-lg" style={{ background: "var(--bg)" }}>
                      <span className="flex-1 text-sm truncate">{m.name}</span>
                      <button onClick={() => setLessonModal({ ...lessonModal, materials: lessonModal.materials.filter((_, j) => j !== i) })}>
                        <X size={14} style={{ color: "#dc2626" }} />
                      </button>
                    </div>
                  ))}
                </div>
                <label className="btn-secondary cursor-pointer w-full mt-2" style={{ fontSize: "0.85rem", padding: "0.5rem 1rem" }}>
                  <Plus size={12} /> Adicionar material
                  <input type="file" hidden onChange={addMaterial} />
                </label>
              </div>
              <button onClick={saveLesson} className="btn-primary w-full" data-testid="save-lesson">Salvar aula</button>
            </div>
          </Modal>
        )}
      </div>
    </AdminLayout>
  );
}

function Modal({ children, title, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white rounded-2xl p-8 max-w-lg w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-serif text-2xl mb-5" style={{ color: "var(--text-primary)" }}>{title}</h3>
        {children}
      </div>
    </div>
  );
}
